import { useRef } from 'react';
import clsx from 'clsx';
import { IAnyType } from 'common/types/common';
import type { DropdownInterface, DropdownOptions } from 'flowbite';
import type { InstanceOptions } from 'flowbite';

export const Select = ({
  className,
  classNameDropdownContainer,
  classNameDropdown,
  label,
  value,
  onChange,
  name,
  errorMessage,
  options,
  isValid,
}: IAnyType) => {
  const dropdownRef = useRef<IAnyType>(null);

  const onClick = async () => {
    const targetEl = document.getElementById(`dropdown-${name}`);
    const triggerEl = document.getElementById(`dropdown-button-${name}`);
    const { Dropdown } = await import('flowbite');
    const optionsClick = {
      placement: 'bottom-start',
      triggerType: 'click',
      offsetSkidding: 0,
      offsetDistance: 10,
    } as DropdownOptions;
    const instanceOptions: InstanceOptions = { id: 'dropdownMenu', override: true };
    if (targetEl) {
      const dropdown: DropdownInterface = new Dropdown(
        targetEl,
        triggerEl,
        optionsClick,
        instanceOptions,
      );
      dropdownRef.current = dropdown;
      dropdown.toggle();
    }
  };

  const handleChange = (item: IAnyType) => () => {
    onChange(item);
    dropdownRef.current.toggle();
  };

  return (
    <div className={clsx('mb-3 w-full', className)}>
      <div className="relative w-full">
        <div
          id={`dropdown-button-${name}`}
          className={clsx(
            'autofill-transparent focus:ring-0; peer block h-[68px] w-full appearance-none overflow-hidden rounded-lg border border-neutral-300 bg-transparent px-3 pb-4 pr-12 pt-6 text-white focus:border-primary focus:outline-none',
            classNameDropdownContainer,
            { 'input-error': !!errorMessage, 'input-success': isValid },
          )}>
          <label
            htmlFor={name}
            dangerouslySetInnerHTML={{ __html: label }}
            className={clsx(
              'white text-md pointer-events-none absolute z-10 origin-[0] transform pl-0.5 font-normal text-white opacity-90 duration-300',
              {
                'top-1/2 -translate-y-1/2 scale-100': !value?.value,
                'top-4 -translate-y-4 scale-[0.65] px-2 text-primary': !!value?.value,
              },
            )}
          />
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {value?.label || ' '}
          </div>
          <div className="absolute bottom-0 left-0 right-0 top-0" onClick={onClick} />
        </div>

        <div
          id={`dropdown-${name}`}
          className={clsx(
            'z-50 hidden w-full max-w-[500px] divide-y divide-gray-100 rounded-lg border-[1px] border-white/20 bg-[#1C1027]',
            classNameDropdown,
          )}>
          <ul className="w-full py-4 text-[16px] text-white">
            {options.map((item: IAnyType, index: number) => (
              <li
                key={item.value}
                className={clsx('block w-full cursor-pointer px-4 py-2 hover:opacity-70', {
                  'border-t-[1px] border-white/10': index > 0,
                  'text-primary': value?.value === item.value,
                })}
                onClick={handleChange(item)}>
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {!!errorMessage && (
        <div className="ml-2 mt-1.5 text-xs text-red-600">{errorMessage || 'Message'}</div>
      )}
    </div>
  );
};
